import {WIDGET_KEYS} from "../../../other/constants/WidgetKeys.jsx";
import { cellMenuItemCopyContactToClipboard, cellMenuItemRemoveRow } from "../../../other/utils/gridCellMenuUtils.jsx";
import {
  defaultCellRenderer as renderer, defaultCellRenderer,
  defaultGroupRenderer as groupRenderer,
  EntityMainNameDefault, lineThroughRenderer, showConfirmationDialog,
  copyTextToClipboard
} from "../../../other/utils/utils.jsx";
import {assignViewsState, canOpenEditor, getEntityShareLink} from "../../../other/utils/otherUtils.jsx";
import { DEEP_LINK_URL_KEYS, SWIVU_ASSIST_CONFIG } from "../../../other/constants/OtherKeys.jsx";
import {EDITOR_KEYS} from "../../../other/constants/EditorKeys.jsx";
import { DateHelper, MessageDialog, Store } from "@bryntum/schedulerpro";
import {GRANTS} from "../../../other/constants/PrivilegeTypes.jsx";
import {updateTabTitleWithRecordCount} from "../../../other/utils/editorUtils.jsx";
import { SwivuStoreCanViewMixin } from "../../stores/mixins/SwivuStoreCanViewMixin.jsx";
import { duplicateNote } from "../../../network/network.jsx";

const tabTitle = "Notes";

const entityNotesRowExpander = {
  dataField: "noteDetails",
  columnPosition: "first",
  column: {
    id: "notes-row-expander",
    draggable: false,
    minWidth: 50
  },
  widget: {
    ref: "noteDetailGrid",
    type: WIDGET_KEYS.swivuPreferenceStyleGrid,
    autoHeight: true,
    columns: [
      {
        id: "description-detail",
        field: "description",
        text: "Description",
        flex: 1,
        editor: true,
        renderer: ({ value }) => defaultCellRenderer({ value }),
      },
      {
        id: "size",
        field: "size",
        text: "Size",
        flex: 1,
        editor: false,
      },
      {
        id: "last-update",
        flex: 1,
        text: "Last Update",
        field: "lastUpdatedAt",
        editor: false,
        renderer(e) {
          return e.value ? DateHelper.format(new Date(e.value), 'MM/DD/YYYY'): renderer({ value: null });
        },
      },
      { id: "last-updated-by", flex: 1, text: "Last Updated By", field: "lastUpdatedBy", editor: false, renderer },
    ],
    maxWidth: "100%",
    features: {
      cellMenu: false,
      sort: false,
      filter: false,
      group: false,
    },
  }
}

export const HasNoteBaseMixin = (base) => (
  class extends base {
    static get $name() {
      return "HasNoteBaseMixin";
    }

    get swivuDeepLinkSlug() {
      return "note";
    }

    static get configurable() {
      return {
        title: tabTitle,
        tab : {
          icon: "fa-regular fa-note-sticky w-4 h-4"
        },
        items: {
          noteGrid: {
            height: "100%",
            type: WIDGET_KEYS.swivuModernGrid,
            swivuGridTitle: "Notes",
            destroyStore: true,
            columns: [
              {
                id: "name",
                field: "name",
                text: "Name",
                flex: 1,
                type: WIDGET_KEYS.swivuSortColumn,
                editor: false,
                groupRenderer,
                renderer: ({ value, record }) => EntityMainNameDefault(lineThroughRenderer(value, record.archived)),
              },
              {
                id: "added-by",
                field: "addedBy",
                text: "Added By",
                flex: 1,
                editor: false,
                renderer,
                groupRenderer,
              },
              {
                id: "can-edit",
                field: "canEdit",
                text: "Can Edit",
                type: WIDGET_KEYS.swivuNoteChangeRoleColumn,
              },
              {
                id: "can-view",
                field: "canView",
                text: "Can View",
                type: WIDGET_KEYS.swivuNoteChangeRoleColumn,
                finalizeCellEdit: "up.onFinalizeCellEdit"
              },
              {
                id: "aging",
                field: "aging",
                text: "Age",
                flex: 1,
                editor: false,
                renderer(e) {
                  return DateHelper.formatCount(e.value, 'day');
                },
                groupRenderer({ groupRowFor, count }) {
                  return groupRowFor ? groupRenderer({ groupRowFor: DateHelper.formatCount(groupRowFor, 'day'), count }): defaultCellRenderer({ value: null });
                },
              },
            ],
            listeners: {
              cellDblClick: "up.onCellDoubleClickHandler",
              beforeCellEditStart: "up.onBeforeCellEditStart",
              finishCellEdit: "up.onCellEditFinish"
            },
            selectionMode: {
              multiSelect: false,
            },
            features: {
              // Todo: whenever rowReorder is enabled make sure we are adding an order whenever record is added or duplicated, right now we are not adding the order
              rowReorder: { showGrip: true, disabled: true },
              rowExpander: entityNotesRowExpander,
              cellMenu: {
                items: {
                  cut: false,
                  copy: false,
                  paste: false,
                  removeRow: cellMenuItemRemoveRow({
                    onItem: "up.onDeleteNoteRecord",
                    icon: "fa-regular fa-trash !text-danger"
                  }),
                  duplicateRecord: {
                    text: "Duplicate Note",
                    onItem: 'up.onDuplicateNoteRecord',
                    icon: "fa-regular fa-clone"
                  },
                  copyLink: cellMenuItemCopyContactToClipboard({ onItem: "up.onCopyFileLink", text: "Copy Link" })
                }
              },
            },
            tbar: {
              items: {
                addButton: {
                  onClick: "up.onAddNoteRecord",
                  cls: 'swivu-modern-button b-blue b-raised',
                  text: "Add Note",
                  icon: "b-fa b-fa-plus b-button-icon",
                }
              }
            },
          }
        },
        swivuAssistance: SWIVU_ASSIST_CONFIG.notes
      }
    }

    onCopyFileLink({ record }) {
      const {linkOnly} = getEntityShareLink({
        record: record.entity,
        context: this,
        editorTabs: "note",
        entityType:  this.deepLinkUrlKey,
        otherKeys: { [DEEP_LINK_URL_KEYS.note]: record.id },
        entityDetail: { name: record.name }
      })
      copyTextToClipboard(linkOnly, `Link to ${record.name} copied to clipboard successfully!`)
    }

    displayPermissionNotAllowed(keyMsg) {
      const currentRole = this.boardManager.selectedBoard.record.roleNames;
      MessageDialog.alert({ title: "Permission Denied!", message: `Your role is "${currentRole}", and You don't have permission to ${keyMsg} this record.` });
    }

    onFinalizeCellEdit({ value, record }) {
      if (value < record.canEdit) {
        return "View permission cannot be higher than Edit permission"
      }
      return true;
    }

    onCellEditFinish({ editorContext: { record } }) {
      if (record.canView < record.canEdit) {
        record.canView = record.canEdit;
      }
    }

    openNoteEditor(record) {
      this.editorHandler.openEditor(EDITOR_KEYS.richTextEditor, record, { entityRecord: this.record, noteRecord: record }, { by: "recordId", useExisting: true });
    }

    onBeforeCellEditStart({ editorContext: { record } }) {
      const canChangeRole = this.user.can(record.canEdit);
      if (!canChangeRole) {
        if (this.user.can(record.canView)) {
          this.openNoteEditor(record);
          setTimeout(() => {
            this.displayPermissionNotAllowed("update");
          }, 300);
        } else {
          this.displayPermissionNotAllowed("update");
        }
      }
      return canChangeRole;
    }

    openNote(record) {
      if (this.user.can(record.canView)) {
        this.openNoteEditor(record);
      } else {
        this.displayPermissionNotAllowed("view");
      }
    }

    loadRecord(record, context, otherParams) {
      /* Calling super.loadRecord to update reference of context if it has changed; Because else wise if we try to use objects from context like editor handler there reference becomes old and causes issues, therefore keeping super.loadRecord before the firstShow check */
      super.loadRecord(record, context);
      if (!otherParams.isFirstShow) return; // opening a note editor below from deeplink cause this loadRecord to call again and again as editor re-renders


      const { noteGrid } = this.widgetMap;
      noteGrid.stateId = this.stateId;

      noteGrid.columns.getById("can-edit").onLoad(context.project, { context });
      noteGrid.columns.getById("can-view").onLoad(context.project, { context });


      noteGrid.store = new (SwivuStoreCanViewMixin(Store, context.user)) ({
        masterStore: context.project.boardNotesStore,
        chainedFilterFn: (bn) => bn[this.recordType] === this.record.id,
      });

      noteGrid.store.applyState({});
      assignViewsState([noteGrid], this.stateProvider);
      this.swivuNoteGridUpdateListener = updateTabTitleWithRecordCount(this, noteGrid.store, tabTitle);

      if (otherParams.note) {
        setTimeout(() => {
          this.openNote(otherParams.note);
        }, 200);
      }
    }

    onCellDoubleClickHandler({ record, column }) {
      if (!record.isPhantom && canOpenEditor(column, record)) {
        this.openNote(record);
      }
    }

    onAddNoteRecord() {
      if (!this.record.isPhantom) {
        const notePhantomRecord = this.project.boardNotesStore.createRecord({ [this.recordType]: this.record.id });
        this.openNoteEditor(notePhantomRecord);
      }
    }

    onDuplicateNoteRecord() {
      const {noteGrid} = this.widgetMap;

      const selectedRecord = noteGrid.selectedRecord;

      if (selectedRecord) {
        const notePhantomRecord = this.project.boardNotesStore.createRecord({
          [this.recordType]: this.record.id,
          name: `Copy of ${selectedRecord.name}`,
          archived: selectedRecord.archived,
        });
        noteGrid.store.add(notePhantomRecord);

        const detacher = noteGrid.store.on("idChange", () => {
          duplicateNote(notePhantomRecord.id, {
            board: selectedRecord.boardId,
            archived: selectedRecord.archived,
            duplicate_id: selectedRecord.id
          })
          detacher();
        });
      }
    }

    onDeleteNoteRecord() {
      const { noteGrid } = this.widgetMap;

      if (noteGrid.selectedRecord) {
        if (this.user.can(noteGrid.selectedRecord.canEdit)) {
          showConfirmationDialog({
            title: `Delete Note`,
            message: `<div>Are you sure you want to delete "${defaultCellRenderer({ value: noteGrid?.selectedRecord?.name })}" note?</div>`,
            cancelBtnText: "Cancel",
            confirmBtnText: "Delete",
            isCloseByHeader: true,
          }).then((isDelete) => {
            if (isDelete) {
              noteGrid.store.remove(noteGrid.selectedRecord.id);
            }
          })
        } else {
          this.displayPermissionNotAllowed("delete");
        }
      }
    }

    swivuCheckGrants(user) {
      if (!user.can(GRANTS.grant_four)) {
        const { noteGrid } = this.widgetMap;
        noteGrid.bbar.widgetMap.addButton.disabled = true;
      }
    }

    onSwivuEditorHide() {
      super.onSwivuEditorHide();
      if (this.swivuNoteGridUpdateListener) {
        this.swivuNoteGridUpdateListener();
      }
    }
  }
)
